import { Router } from '@angular/router';

import { SupportRequestCategory, SupportRequestWizardService } from './support-request-wizard.service';

/** Absolute route paths for all support-request pages. */
export const SR_ROUTES = {
  home:            '/dashboard/support-request',
  step2:           '/dashboard/support-request/step-2',
  wpNeedSelect:    '/dashboard/support-request/step-3/wordpress',
  wpDomainHosting: '/dashboard/support-request/wordpress/domain-hosting',
  wpAssets:        '/dashboard/support-request/wordpress/assets',
  wpSubmission:    '/dashboard/support-request/wordpress/submission',
  thankYou:        '/dashboard/support-request/thank-you',
  dashboard:       '/dashboard',
};

/**
 * i18n key for the middle breadcrumb segment of the WordPress sub-flow.
 *
 * @description The sub-flow is shared between the visibility and capacity
 * categories — the trail middle reads differently depending on which category
 * the user entered from.
 */
export function getWpTrailMiddleKey(category: SupportRequestCategory | null): string {
  return category === 'capacity'
    ? 'SupportRequestStep3-TRAIL_CAPACITY'
    : 'SupportRequestStep3-TRAIL_VISIBILITY';
}

/**
 * Injects `class="nss-option-icon"` into an SVG string so icon sizing
 * styles apply uniformly across Step 1 and Step 2 option tiles.
 */
export function withOptionIconClass(svgMarkup: string): string {
  return svgMarkup.replace('<svg xmlns', '<svg class="nss-option-icon" xmlns');
}

/**
 * Returns `true` if `val` looks like a valid URL.
 *
 * Accepts inputs with or without a protocol prefix - bare hostnames such as
 * `www.example.com` are normalized to `https://www.example.com` before
 * parsing. Requires at least one dot in the hostname; the browser's built-in
 * `URL` constructor accepts bare labels like `https://twi` without throwing,
 * so without this check they would pass.
 */
export function isValidHttpUrl(val: string): boolean {
  const trimmed = val.trim();
  
  if (!trimmed) return false;
  
  const normalized = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  
  try {
    const url = new URL(normalized);
    return url.hostname.includes('.');
  } catch {
    return false;
  }
}

/**
 * Returns `true` if `val` contains between 7 and 15 digits.
 *
 * Separators (spaces, hyphens, parentheses, dots) are stripped before counting
 * so any international format is accepted. 7 is the minimum for a local number;
 * 15 is the E.164 maximum.
 */
export function isValidPhone(val: string): boolean {
  const digits = val.replace(/\D/g, '');
  return digits.length >= 7 && digits.length <= 15;
}

/**
 * Returns `true` if `val` looks like a plausible email address.
 *
 * Intentionally minimal: requires a non-whitespace local part, `@`, a domain,
 * and at least one dot in the domain. Catches obvious garbage without
 * reimplementing RFC 5321.
 */
export function isValidEmail(val: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.trim());
}

/** Resets wizard state and navigates to the main dashboard. */
export function cancelWizard(wizard: SupportRequestWizardService, router: Router): void {
  wizard.reset();
  router.navigate([SR_ROUTES.dashboard]);
}
